<template>
  <section
    v-if="status === 'success'"
    class="radar"
  >
    <SectionTitle title="月毎の気温と降水量" />
    <div class="inner">
      <div class="content">
        <div class="block">
          <p class="heading">
            {{ pointNameJa }}
          </p>
          <div class="graph">
            <figure>
              <PointNormalsChart
                :data="data.normals.data[pointId]"
                :config="data.normals.graphConfig"
                display-scale="temp"
              />
            </figure>
          </div>
        </div>
        <div class="block">
          <div class="heading">
            <div class="select">
              <select
                v-model="selectedCompareCity"
                name="city"
              >
                <option
                  v-for="city in compareCityList"
                  :key="city"
                  :value="city"
                >
                  {{ compareCityLabel[city] }}
                </option>
              </select>
            </div>
          </div>
          <div class="graph">
            <figure>
              <PointNormalsChart
                :data="data.normals.data[selectedCompareCity]"
                :config="data.normals.graphConfig"
                display-scale="prec"
              />
            </figure>
          </div>
        </div>
      </div>
      <div class="legend">
        <div class="legend__item">
          <div
            class="legend__icon"
            data-type="max"
          />
          <p class="legend__text">
            最高気温
          </p>
        </div>
        <div class="legend__item">
          <div
            class="legend__icon"
            data-type="min"
          />
          <p class="legend__text">
            最低気温
          </p>
        </div>
        <div class="legend__item">
          <div
            class="legend__icon"
            data-type="prec"
          />
          <p class="legend__text">
            降水量
          </p>
        </div>
      </div>
      <p class="note">
        ※各月の降水量、気温は1991～2020年の平均値です
      </p>
    </div>
  </section>
</template>

<script setup>
import dayjs from 'dayjs'

const props = defineProps({
  pointNameJa: {
    type: String,
    required: true
  },
  pointId: {
    type: String,
    required: true
  },
  now: {
    type: Number,
    default: dayjs().unix()
  }
})

const selectedCompareCity = ref('tokyo')

const compareCityList = [
  'sapporo',
  'sendai',
  'kanazawa',
  'tokyo',
  'nagoya',
  'osaka',
  'hiroshima',
  'takamatsu',
  'fukuoka',
  'naha'
]

const compareCityLabel = {
  sapporo: '札幌',
  sendai: '仙台',
  kanazawa: '金沢',
  tokyo: '東京',
  nagoya: '名古屋',
  osaka: '大阪',
  hiroshima: '広島',
  takamatsu: '高松',
  fukuoka: '福岡',
  naha: '那覇'
}

const { data, status } = await useAsyncData(
  `PointMonthlyTemp:${props.pointId}:${compareCityList.join(',')}`,
  async () => {
    const normalsDataTask = $fetch(`/api/normals?globalPointId=${props.pointId}&ss1PointId=${compareCityList.join(',')}`)

    const [normals] = await Promise.all([normalsDataTask])
    return {
      normals
    }
  }
)
</script>

<style lang="scss" scoped>
.radar {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .content {
    display: flex;
    overflow-x: scroll;
    border-top: 1px solid #B4BECD;
    &::-webkit-scrollbar {
      height: 4px;
      width: 4px
    }
    &::-webkit-scrollbar-track {
      background-color: transparent;
      border-radius: 4px
    }
    &::-webkit-scrollbar-thumb {
      background-color: #438fec;
      border-radius: 4px
    }
  }
  .block {
    display: flex;
    flex-direction: column;
    flex: 1;
    &:first-child {
      border-left: 1px solid #B4BECD;
    }
  }
  .heading {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding-block: 5px;
    background-color: #eef1f8;
    border-bottom: 1px solid #B4BECD;
    border-right: 1px solid #B4BECD;
    color: #101010;
    font-size: 14px;
    font-weight: 600;
    line-height: 24px;
    text-align: center;
    .select {
      position: relative;
      display: inline-block;
      border-radius: 100vmax;
      border: 1px solid #E3E6E9;
      background-color: #FFF;
      cursor: pointer;
      select {
        display: block;
        width: 100%;
        padding: 3px 20px;
        color: #303030;
        text-align: center;
        line-height: 1em;
        outline: none;
        cursor: pointer;
      }
      &::before {
        position: absolute;
        top: 50%;
        right: 5px;
        margin-top: -3px;
        width: 9px;
        height: 6px;
        clip-path: polygon(0 0, 100% 0, 50% 100%);
        background-color: #3569C0;
        content: '';
      }
    }
  }
  .graph {
    min-width: 290px;
    padding: 5px 10px;
    border-bottom: 1px solid #B4BECD;
    border-right: 1px solid #B4BECD;
    figure {
      position: relative;
      aspect-ratio: 1.1;
    }
  }
  .legend {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 30px;
    margin-top: -4px;
    padding: 12px 8px 8px;
    border-bottom: 1px solid #B4BECD;
    border-left: 1px solid #B4BECD;
    border-right: 1px solid #B4BECD;
    &__item {
      display: flex;
      align-items: center;
      gap: 5px;
    }
    &__text {
      color: #303030;
      font-size: 12px;
      font-weight: 600;
      line-height: 1em;
    }
    &__icon {
      position: relative;
      width: 30px;
      &[data-type="max"] {
        height: 2px;
        background-color: #F64D00;
        &::after {
          position: absolute;
          top: 50%;
          left: 50%;
          margin-top: -4px;
          margin-left: -4px;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #F64D00;
          content: '';
        }
      }
      &[data-type="min"] {
        height: 2px;
        background-color: #0075F3;
        &::after {
          position: absolute;
          top: 50%;
          left: 50%;
          margin-top: -4px;
          margin-left: -4px;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #0075F3;
          content: '';
        }
      }
      &[data-type="prec"] {
        height: 10px;
        background-color: #D4EAFF;
      }
    }
  }
  p.note{
    color: #303030;
    font-size: 12px;
    line-height: 1em;
    padding-top: 1px;
  }
}
</style>
